import { faBullhorn, faClose } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import ModalPortal from "../../components/Modal/ModalPortal"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { NoticeEntity } from "../../Objects/Notice/NoticeEntity"
import { IPublicMenuTheme, isLegacyPublicMenuTheme } from "../Helpers/PublicMenuTheme"

interface NoticeDetailsPupopProps {
  notice: NoticeEntity
  theme?: IPublicMenuTheme | null
  onClose: () => void
}

export const NoticeDetailsPupop: React.FC<NoticeDetailsPupopProps> = ({ notice, theme, onClose }) => {
  const { t } = useTranslation()

  const isLegacyTheme = !theme || isLegacyPublicMenuTheme(theme)

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay} onClick={onClose}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section
            className={defaultStyle.alert1}
            style={isLegacyTheme || !theme ? undefined : { backgroundColor: theme.surfaceColor, color: theme.textColor }}
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2"
              style={isLegacyTheme || !theme ? undefined : { borderColor: theme.borderColor }}
            >
              <div className="flex min-w-0 items-center gap-2">
                <FontAwesomeIcon
                  icon={faBullhorn}
                  className="shrink-0 text-amber-600"
                  style={isLegacyTheme || !theme ? undefined : { color: theme.accentColor }}
                />

                <h2
                  className="truncate text-sm font-black uppercase tracking-wide text-gray-900"
                  style={isLegacyTheme || !theme ? undefined : { color: theme.textColor }}
                >
                  {t("publicMenu:notice.label")}
                </h2>
              </div>

              <button
                type="button"
                onClick={onClose}
                aria-label={t("publicMenu:notice.close")}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-600 text-white transition hover:bg-red-700"
                style={
                  isLegacyTheme || !theme ? undefined : { backgroundColor: theme.buttonColor, color: theme.buttonTextColor }
                }
                data-interaction-description-key="interactionDescriptions:publicMenu.pupop.noticeDetailsPupop.button.onClose"
              >
                <FontAwesomeIcon icon={faClose} />
              </button>
            </div>

            <div className="flex flex-col items-center gap-3 p-4 text-center sm:flex-row sm:items-start sm:text-left">
              {notice.iconUrl && (
                <img
                  src={notice.iconUrl.toClientRequest()}
                  alt={notice.name}
                  className="mx-auto h-24 w-24 shrink-0 rounded-lg border object-cover sm:mx-0"
                  style={isLegacyTheme || !theme ? undefined : { borderColor: theme.borderColor }}
                  loading="lazy"
                  decoding="async"
                />
              )}

              <div className="w-full space-y-2">
                <h3
                  className="text-base font-black text-gray-900"
                  style={isLegacyTheme || !theme ? undefined : { color: theme.textColor }}
                >
                  {notice.name}
                </h3>

                <p
                  className="whitespace-pre-wrap break-words text-justify text-sm text-gray-700"
                  style={isLegacyTheme || !theme ? undefined : { color: theme.textColor, opacity: 0.85 }}
                >
                  {notice.description}
                </p>
              </div>
            </div>
          </section>
        </Animate>
      </div>
    </ModalPortal>
  )
}
